/**
 * FollowSuggest - 关注建议页面
 */

import { useParams, useSearchParams, useNavigate } from 'react-router-dom'
import { useEvent } from '../hooks/useEvents'
import { DetailLoadingState, DetailErrorState } from '../components/StateComponents'
import { getNarrativeConfig } from '../utils/category'
import { isInWatchlist, addToWatchlist, removeFromWatchlist } from '../utils/storage'
import { useState, useEffect } from 'react'
import type { MentionedAccount } from '../types/event'
import { useLanguage } from '../hooks/useLanguage'

export function FollowSuggest() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  
  const date = searchParams.get('date') || 'latest'
  const narrative = searchParams.get('narrative') || ''
  const { t } = useLanguage()
  
  const { data: event, isLoading, error } = useEvent(id)
  
  // 关注状态
  const [watchlistStatus, setWatchlistStatus] = useState<Record<string, boolean>>({})

  // 初始化关注状态
  useEffect(() => {
    if (event?.mentioned_by_accounts) {
      const status: Record<string, boolean> = {}
      event.mentioned_by_accounts.forEach(acc => {
        status[acc.screen_name] = isInWatchlist(acc.screen_name)
      })
      setWatchlistStatus(status)
    }
  }, [event?.mentioned_by_accounts])

  // 添加/移除关注
  const handleToggle = (account: MentionedAccount) => {
    const isCurrentlyInWatchlist = watchlistStatus[account.screen_name]
    
    if (isCurrentlyInWatchlist) {
      removeFromWatchlist(account.screen_name)
      setWatchlistStatus(prev => ({ ...prev, [account.screen_name]: false }))
    } else {
      addToWatchlist({
        screen_name: account.screen_name,
        layer: account.layer,
        source_event_id: id,
      })
      setWatchlistStatus(prev => ({ ...prev, [account.screen_name]: true }))
    }
  }

  // 批量添加
  const handleAddAll = () => {
    if (!event?.mentioned_by_accounts) return
    
    let addedCount = 0
    event.mentioned_by_accounts.forEach(acc => {
      if (!watchlistStatus[acc.screen_name]) {
        addToWatchlist({
          screen_name: acc.screen_name,
          layer: acc.layer,
          source_event_id: id,
        })
        addedCount++
      }
    })
    
    // 更新状态
    const newStatus: Record<string, boolean> = {}
    event.mentioned_by_accounts.forEach(acc => {
      newStatus[acc.screen_name] = true
    })
    setWatchlistStatus(newStatus)
  }

  if (isLoading) {
    return <DetailLoadingState />
  }

  if (error || !event) {
    return <DetailErrorState onBack={() => navigate(`/event/${id}?date=${date}&narrative=${narrative}`)} />
  }

  const config = getNarrativeConfig(event.narrative)
  const accounts = event.mentioned_by_accounts || []

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
      {/* Header */}
      <header className="sticky top-0 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 z-10">
        <div className="max-w-3xl mx-auto flex items-center gap-3 p-3">
          <button
            onClick={() => navigate(`/event/${id}?date=${date}&narrative=${narrative}`)}
            className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 dark:bg-gray-800"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <div className="flex-1">
            <h1 className="text-base font-bold text-gray-900 dark:text-white">{t('关注建议', 'Follow Suggestions')}</h1>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {t('基于当前事件的高价值账号', 'High-value accounts from this event')}
            </p>
          </div>
        </div>
      </header>

      <main className="p-4 max-w-3xl mx-auto space-y-3">
        {/* 事件信息 */}
        <div className="bg-white dark:bg-gray-800 rounded-xl p-3">
          <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400 mb-1">
            <span className={config.color}>{config.emoji}</span>
            <span>{config.name}</span>
          </div>
          <h2 className="text-sm font-semibold text-gray-900 dark:text-white line-clamp-2">
            {event.title || t('未命名事件', 'Untitled Event')}
          </h2>
        </div>

        {/* 说明 */}
        <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-3 text-sm text-blue-700 dark:text-blue-300">
          <p>
            {t(
              '💡 基于当前事件中提到的账号，以下是建议关注的高价值账号列表。点击"加入关注池"可以将账号保存到本地关注池中。',
              '💡 Based on the accounts mentioned in this event, here are high-value accounts worth following. Tap "Add" to save an account to your local watchlist.'
            )}
          </p>
        </div>

        {/* 账号列表 */}
        {accounts.length > 0 ? (
          <>
            {/* 批量操作 */}
            <div className="flex justify-end">
              <button
                onClick={handleAddAll}
                className="px-3 py-1.5 bg-blue-500 text-white rounded-lg text-xs font-medium hover:bg-blue-600 transition-colors"
              >
                {t('全部加入关注池', 'Add All')}
              </button>
            </div>

            {/* 账号卡片 */}
            <div className="space-y-2">
              {accounts.map((account, i) => (
                <div
                  key={i}
                  className="bg-white dark:bg-gray-800 rounded-xl p-3 flex items-center justify-between"
                >
                  <div className="flex items-center gap-3">
                    {/* 序号 */}
                    <span className="text-xs text-gray-400 font-medium">
                      {i + 1}
                    </span>
                    
                    {/* 账号信息 */}
                    <div className="flex-1 min-w-0">
                      <a
                        href={`https://twitter.com/${account.screen_name}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm font-medium text-blue-600 dark:text-blue-400 hover:underline truncate"
                      >
                        @{account.screen_name}
                      </a>
                      <div className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                        {account.layer && (
                          <span className={`px-1.5 py-0.5 rounded text-xs ${
                            account.layer === 'layer0' ? 'bg-yellow-100 text-yellow-700' :
                            account.layer === 'layer1' ? 'bg-purple-100 text-purple-700' :
                            'bg-gray-100 text-gray-600'
                          }`}>
                            {account.layer}
                          </span>
                        )}
                        {account.engagement && (
                          <span>{t(`互动: ${account.engagement.toLocaleString()}`, `Engagement: ${account.engagement.toLocaleString()}`)}</span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* 操作按钮 */}
                  <button
                    onClick={() => handleToggle(account)}
                    className={`px-2.5 py-1 rounded-lg text-xs font-medium transition-colors ${
                      watchlistStatus[account.screen_name]
                        ? 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200'
                    }`}
                  >
                    {watchlistStatus[account.screen_name] ? t('已关注', 'Following') : t('加入', 'Add')}
                  </button>
                </div>
              ))}
            </div>
          </>
        ) : (
          <div className="bg-white dark:bg-gray-800 rounded-xl p-8 text-center text-gray-500 dark:text-gray-400">
            <p>{t('该事件暂无提及账号', 'No mentioned accounts for this event')}</p>
          </div>
        )}

        {/* 返回按钮 */}
        <button
          onClick={() => navigate(`/event/${id}?date=${date}&narrative=${narrative}`)}
          className="w-full py-2 bg-gray-100 dark:bg-gray-800 rounded-xl text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
        >
          {t('返回事件详情', 'Back to Event Details')}
        </button>
      </main>
    </div>
  )
}
